import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme,
  Autocomplete,
} from '@mui/material';
import { FaTimes } from 'react-icons/fa';
import { parentCategories, statusOption } from '../../Helpers/Constant/CategoryContant';
import DateRangeFilter from "../DateRangeFilter";
import { useLazyGetAllFilterCategoryListQuery, useLazyGetAllDynamicFilterCategoryListQuery, useDeleteCategoryMutation, useAddAndUpdateCategoryMutation } from '../../Services/CategoryService';

interface CategoryFilters {
  parentCategory: string;
  status: string;
  search: string;
  startDate?: string;
  endDate?: string;
}

interface CategorySlidingFilterProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: CategoryFilters) => void;
  onClearFilters: () => void;
  filters: CategoryFilters;
  onFilterChange: (filterName: keyof CategoryFilters, value: any) => void;
}

const CategorySlidingFilter: React.FC<CategorySlidingFilterProps> = ({
  open,
  onClose,
  onApplyFilters,
  onClearFilters,
  filters,
  onFilterChange,
}) => {
  const theme = useTheme();
  const [isDateRangeValid, setIsDateRangeValid] = useState(true);

  // State for autocomplete
  const [searchOptions, setSearchOptions] = useState<string[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const [triggerGetCategoryList, { data: categoryList, error, isLoading, reset }] = useLazyGetAllDynamicFilterCategoryListQuery();

  // Function to get search value from category name
  const getSearchValue = useCallback((value: string) => {
    return value || '';
  }, []);

  // Search categories based on input with debouncing
  const searchCategories = useCallback(async (searchValue: string) => {
    if (categoryLoading) return;
    
    setCategoryLoading(true);
    try {
      const filters = searchValue.trim() 
        ? [{ key: "categoryName", value: searchValue.trim() }] 
        : [];
      
      const result = await triggerGetCategoryList({
        page: 1,
        pageSize: 100,
        filters: filters,
      }).unwrap();
      
      if (result?.data) {
        const categoryNames = result.data.map((category: any) => category.categoryName);
        setSearchOptions(categoryNames);
        setHasMoreData(result.data.length === 100);
      }
    } catch (error) {
    } finally {
      setCategoryLoading(false);
    }
  }, [triggerGetCategoryList, categoryLoading]);

  // Debounced search function
  const debouncedSearch = useCallback((searchValue: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      searchCategories(searchValue);
    }, 300); // 300ms debounce
    
    setSearchTimeout(timeout);
  }, [searchTimeout, searchCategories]);

  // Load all categories for autocomplete (initial load)
  const loadAllRecords = useCallback(async () => {
    if (categoryLoading) return;
    
    setCategoryLoading(true);
    try {
      const result = await triggerGetCategoryList({
        page: 1,
        pageSize: 100,
        filters: [],
      }).unwrap();
      
      if (result?.data) {
        const categoryNames = result.data.map((category: any) => category.categoryName);
        setSearchOptions(categoryNames);
        setHasMoreData(result.data.length === 100);
      }
    } catch (error) {
    } finally {
      setCategoryLoading(false);
    }
  }, [triggerGetCategoryList, categoryLoading]);

  // Load more data for pagination
  const loadMoreData = useCallback(async () => {
    if (isLoadingMore || !hasMoreData) return;
    
    setIsLoadingMore(true);
    try {
      const currentPage = Math.floor(searchOptions.length / 100) + 1;
      const result = await triggerGetCategoryList({
        page: currentPage + 1,
        pageSize: 100,
        filters: searchTerm ? [{ key: "categoryName", value: searchTerm }] : [],
      }).unwrap();
      
      if (result?.data && result.data.length > 0) {
        const newCategoryNames = result.data.map((category: any) => category.categoryName);
        setSearchOptions(prev => [...prev, ...newCategoryNames]);
        setHasMoreData(result.data.length === 100);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      setHasMoreData(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [triggerGetCategoryList, isLoadingMore, hasMoreData, searchOptions.length, searchTerm]);

  const handleApply = () => {
    onApplyFilters(filters);
    onClose();
  };

  const handleClear = () => {
    onClearFilters();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          padding: 0,
        },
      }}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        '& .MuiDrawer-paper': {
          overflow: 'visible',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
          }}
        >
          <Typography variant="h6" component="h2">
            Apply Filters
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: 'white' }}
            size="small"
          >
            <FaTimes />
          </IconButton>
        </Box>

        {/* Filter Content */}
        <Box sx={{ flex: 1, padding: '20px', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            {/* Search Filter */}
            <Box>
              <Autocomplete
                freeSolo
                options={searchOptions}
                value={filters.search || ''}
                onInputChange={(event, newInputValue) => {
                  const searchValue = getSearchValue(newInputValue || '');
                  setSearchTerm(searchValue);
                  onFilterChange('search', searchValue);
                  
                  // Trigger debounced search for suggestions
                  if (searchValue.length >= 2 || searchValue.length === 0) {
                    debouncedSearch(searchValue);
                  }
                }}
                onChange={(event, newValue) => {
                  const searchValue = getSearchValue(newValue || '');
                  setSearchTerm(searchValue);
                  onFilterChange('search', searchValue);
                  
                  // Trigger search when selecting from dropdown
                  if (searchValue.length >= 2 || searchValue.length === 0) {
                    debouncedSearch(searchValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search Categories"
                    placeholder="Search by category name"
                    onFocus={loadAllRecords}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'background.paper',
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: (event: React.SyntheticEvent) => {
                    const listboxNode = event.currentTarget;
                    if (
                      listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight - 10
                    ) {
                      loadMoreData();
                    }
                  },
                  style: {
                    maxHeight: '200px',
                  },
                }}
                sx={{
                  '& .MuiAutocomplete-popper': {
                    zIndex: 9999,
                  },
                }}
                loading={categoryLoading || isLoadingMore}
                loadingText={isLoadingMore ? "Loading more..." : "Loading suggestions..."}
                noOptionsText={hasMoreData ? "Keep typing to search..." : "No matching results"}
              />
            </Box>

            {/* Parent Category Filter */}
            <FormControl fullWidth size="small">
              <InputLabel shrink>Parent Category</InputLabel>
              <Select
                value={filters.parentCategory}
                label="Parent Category"
                displayEmpty
                onChange={(e) => onFilterChange('parentCategory', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      zIndex: 9999,
                    },
                  },
                  disablePortal: true,
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {parentCategories.map((category: any) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl fullWidth size="small">
              <InputLabel shrink>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                displayEmpty
                onChange={(e) => onFilterChange('status', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      zIndex: 9999,
                    },
                  },
                  disablePortal: true,
                }}
              >
                <MenuItem value="All">All</MenuItem>
                {statusOption.map((status: any) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Date Range Filter */}
            {/* <Box sx={{ display: 'flex', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Date Range</Typography>
              <FormControl fullWidth size="small">
                <TextField
                  size="small"
                  label="Start Date"
                  type="date"
                  value={filters.startDate || ''}
                  onChange={(e) => onFilterChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
              <FormControl fullWidth size="small">
                <TextField
                  size="small"
                  label="End Date"
                  type="date"
                  value={filters.endDate || ''}
                  onChange={(e) => onFilterChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Box> */}
            <DateRangeFilter
              startDate={filters.startDate}
              endDate={filters.endDate}
              onChange={onFilterChange}
              label="Date Range"
              fromLabel="Start Date"
              toLabel="End Date"
              onValidityChange={setIsDateRangeValid}
            />

            
          </Box>
        </Box>

        <Divider />

        {/* Footer Actions */}
        <Box
          sx={{
            padding: '16px 20px',
            display: 'flex',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{ flex: 1 }}
          >
            Clear All
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={!isDateRangeValid}
            sx={{ flex: 1 }}
          >
            Apply Filters
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default CategorySlidingFilter;
